import { meterService } from "../services/meterService";
import { appStore } from "../state/store";
import { can } from "../state/session";
import { createOverlay } from "./overlay";
import { createStepController } from "./steps";
import { byId } from "./dom";
import { formatRelative } from "./format";
import type { Meter, MeterStatus } from "../types";

const STATUS_LABEL: Record<MeterStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending Verification",
  fault: "Fault",
};

export function initMetersList(onUnlinked: () => void): void {
  const listEl = byId("my-meters-list");

  function render(meters: Meter[]): void {
    listEl.innerHTML = "";

    if (meters.length === 0) {
      const empty = document.createElement("p");
      empty.className = "hub-detail-note";
      empty.textContent = "No meters linked yet.";
      listEl.appendChild(empty);
      return;
    }

    meters.forEach((meter) => {
      const reading = appStore.state.readings[meter.id];
      const card = document.createElement("div");
      card.className = "hub-facility-card";
      card.innerHTML = `
        <div class="meter-card-top">
          <div>
            <span class="hub-facility-name">${meter.label}</span>
            <span class="hub-facility-meta">Meter ${meter.serial} \u00b7 ${meter.sourceType === "grid" ? "Grid Supply" : "Facility Generator"}</span>
          </div>
          <span class="meter-status-badge meter-status-badge--${meter.status}">${STATUS_LABEL[meter.status]}</span>
        </div>
        <div class="hub-facility-meta">Last synced: ${formatRelative(reading?.lastSyncAt ?? null)}</div>
      `;

      if (can("meter:unlink")) {
        const unlinkBtn = document.createElement("button");
        unlinkBtn.type = "button";
        unlinkBtn.className = "btn-ghost meter-unlink-btn";
        unlinkBtn.textContent = "Unlink";
        unlinkBtn.addEventListener("click", async () => {
          const confirmed = window.confirm(`Unlink ${meter.label} (${meter.serial})? This cannot be undone.`);
          if (!confirmed) return;
          unlinkBtn.disabled = true;
          unlinkBtn.textContent = "Unlinking\u2026";
          try {
            await meterService.unlinkMeter(meter.id);
            onUnlinked();
          } catch {
            unlinkBtn.disabled = false;
            unlinkBtn.textContent = "Unlink";
            window.alert("Couldn't unlink this meter. Please try again.");
          }
        });
        card.appendChild(unlinkBtn);
      }

      listEl.appendChild(card);
    });
  }

  appStore.subscribe((state) => render(state.meters));
}

export interface LinkMeterFlow {
  open(prefillSerial?: string): void;
}

export function initLinkMeterFlow(onLinked: () => void): LinkMeterFlow {
  const overlay = createOverlay("sheet-link-meter");
  const root = byId("link-meter-flow");
  const steps = createStepController(root);

  const serialInput = byId<HTMLInputElement>("link-meter-serial");
  const labelInput = byId<HTMLInputElement>("link-meter-label");
  const formError = byId("link-meter-error");
  const linkBtn = byId<HTMLButtonElement>("link-meter-submit-btn");

  const failureReason = byId("link-meter-failure-reason");
  const retryBtn = byId("link-meter-retry-btn");
  const doneBtn = byId("link-meter-done-btn");
  const successLine = byId("link-meter-success-line");

  linkBtn.addEventListener("click", async () => {
    const serial = serialInput.value.trim();
    if (!serial) {
      formError.textContent = "Enter the meter serial number.";
      formError.hidden = false;
      return;
    }
    formError.hidden = true;
    steps.show("pending");
    try {
      const linked = await meterService.linkMeter({ serial, label: labelInput.value.trim() || undefined });
      successLine.textContent =
        linked.verification === "verified"
          ? "Meter linked and confirmed on the vending system."
          : linked.verification === "not_found"
            ? "Meter linked, but the vending system doesn't know this number yet. Check it, or ask your vending provider to register it. Purchases will fail until it is."
            : "Meter linked. It will show as pending verification until confirmed.";
      onLinked();
      steps.show("success");
    } catch (err) {
      failureReason.textContent = err instanceof Error ? err.message : "Couldn't link this meter.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("form"));
  doneBtn.addEventListener("click", () => overlay.close());

  function open(prefillSerial?: string): void {
    serialInput.value = prefillSerial ?? "";
    labelInput.value = "";
    formError.hidden = true;
    steps.show("form");
    overlay.open();
    if (!prefillSerial) serialInput.focus();
  }

  return { open };
}
